/**
 * Checklists feature entry point
 *
 * Initializes checklist functionality for all Create cards on the page.
 * Handles ingredient checkboxes, instruction checkboxes, and collapsible groups.
 */

import { render, Component } from 'preact'
import { useChecklistState } from './useChecklistState'
import { initIngredientCheckboxes } from './ingredients'
import { initInstructionCheckboxes } from './instructions'
import { initCollapsibleGroups } from './collapsibleGroups'

/**
 * Checklist controller component for a single Create card
 * Uses the useChecklistState hook and initializes DOM handlers
 */
const ChecklistController = ({ creationId, cardElement, sections }) => {
  const {
    isLoaded,
    toggleIngredient,
    toggleStep,
    toggleGroup,
    isIngredientChecked,
    isStepChecked,
    isGroupCollapsed,
  } = useChecklistState(creationId)

  const ingredientsActive = sections === 'ingredients' || sections === 'both'
  const instructionsActive = sections === 'instructions' || sections === 'both'

  // Initialize DOM handlers once state is loaded
  if (isLoaded && cardElement) {
    // Initialize ingredient checkboxes
    if (ingredientsActive) {
      initIngredientCheckboxes(cardElement, isIngredientChecked, toggleIngredient)

      // Initialize collapsible groups (ingredients-only feature)
      initCollapsibleGroups(cardElement, isGroupCollapsed, toggleGroup)
    }

    // Initialize instruction checkboxes
    if (instructionsActive) {
      initInstructionCheckboxes(cardElement, isStepChecked, toggleStep)
    }
  }

  // This component doesn't render anything visible
  // It just manages state and initializes DOM handlers
  return null
}

/**
 * Initialize checklists for a single card
 * @param {HTMLElement} mountPoint - The .mv-create-checklists element
 */
const initChecklistForCard = (mountPoint) => {
  const creationId = mountPoint.getAttribute('data-creation-id')

  if (!creationId) {
    console.warn('Checklist mount point missing data-creation-id attribute')
    return
  }

  // Find the parent card element
  const cardElement = mountPoint.closest('.mv-create-card')

  if (!cardElement) {
    console.warn('Could not find parent .mv-create-card for checklist')
    return
  }

  // Check if already initialized
  if (mountPoint.hasAttribute('data-checklist-initialized')) {
    return
  }

  // Mark as initialized
  mountPoint.setAttribute('data-checklist-initialized', 'true')

  // Read checklist sections setting
  const sections = window.MV_CREATE_SETTINGS?.__OPTIONS__?.checklistSections || 'ingredients'

  // Render the controller component into the mount point
  render(
    <ChecklistController creationId={creationId} cardElement={cardElement} sections={sections} />,
    mountPoint
  )
}

/**
 * Initialize checklists for all cards on the page
 */
const initAllChecklists = () => {
  // Find all checklist mount points
  const mountPoints = document.querySelectorAll('.mv-create-checklists')

  mountPoints.forEach((mountPoint) => {
    initChecklistForCard(mountPoint)
  })
}

/**
 * Default export - initializes all checklists
 * Called by the main client entry point
 */
export default () => {
  // Check if checklists feature is enabled
  const checklistsEnabled =
    window.MV_CREATE_SETTINGS?.__OPTIONS__?.checklistsEnabled

  if (!checklistsEnabled) {
    return
  }

  initAllChecklists()
}

// Named exports for potential direct usage
export { initChecklistForCard, initAllChecklists, ChecklistController }
